import { useState } from 'react'
import Modal from '../../ui/Modal'
import Button from '../../ui/Button'
import { saveCustomDoh } from '../../api/client'
import { validateEndpoint, validateProvider } from '../../lib/customDoh'
import type { CustomResolver } from '../../lib/customDoh'
import { doh } from '../../content/site'
import styles from './AddResolverDialog.module.css'

interface AddResolverDialogProps {
    open: boolean
    onClose: () => void
    onAdded: (resolver: CustomResolver) => void
}

export default function AddResolverDialog({ open, onClose, onAdded }: AddResolverDialogProps) {
    const [provider, setProvider] = useState('')
    const [url, setUrl] = useState('')
    const [touched, setTouched] = useState(false)

    const providerError = touched ? validateProvider(provider) : undefined
    const urlError = touched ? validateEndpoint(url) : undefined

    function submit(e: React.FormEvent) {
        e.preventDefault()
        setTouched(true)
        if (validateProvider(provider) || validateEndpoint(url)) return

        const resolver = { provider: provider.trim(), url: url.trim() }
        // persistence is the hook's job (onAdded), so add and remove write the
        // store the same way and a custom resolver survives reload until the
        // visitor deletes it. best effort: also tell the server, but the resolver
        // is usable locally whether or not the submission is accepted.
        void saveCustomDoh(resolver.provider, resolver.url).catch(() => {})
        onAdded(resolver)

        setProvider(''); setUrl(''); setTouched(false)
        onClose()
    }

    return (
        <Modal open={open} title={doh.dialog.title} onClose={onClose}>
            <form className={styles.form} onSubmit={submit} noValidate>
                <div className={styles.field}>
                    <label htmlFor="doh-provider" className={styles.label}>{doh.dialog.name}</label>
                    <input
                        id="doh-provider"
                        className={styles.input}
                        value={provider}
                        onChange={(e) => setProvider(e.target.value)}
                        aria-invalid={Boolean(providerError)}
                        aria-describedby={providerError ? 'doh-provider-error' : undefined}
                        autoComplete="off"
                    />
                    {providerError && (
                        <p id="doh-provider-error" className={styles.error}>{providerError}</p>
                    )}
                </div>

                <div className={styles.field}>
                    <label htmlFor="doh-url" className={styles.label}>{doh.dialog.url}</label>
                    <input
                        id="doh-url"
                        className={styles.inputMono}
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://example.org/dns-query"
                        aria-invalid={Boolean(urlError)}
                        aria-describedby={urlError ? 'doh-url-error' : undefined}
                        autoComplete="off"
                        spellCheck={false}
                    />
                    {urlError && <p id="doh-url-error" className={styles.error}>{urlError}</p>}
                </div>

                <div className={styles.actions}>
                    <Button onClick={onClose}>{doh.dialog.cancel}</Button>
                    <Button type="submit" variant="primary">{doh.dialog.save}</Button>
                </div>
            </form>
        </Modal>
    )
}
